import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { api } from "@/lib/api";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import VideoCard from "@/components/plyx/VideoCard";
import { Loader2, X, Send, Plus, Sparkles, Info, Trophy, Coins } from "lucide-react";
import { toast } from "sonner";
import { isPlyxStandaloneMode, isPlyxProductionDomain } from "@/lib/plyxStandalone";
import PlyxExitModal from "@/components/plyx/PlyxExitModal";
import FeedSpotlightChip from "@/components/plyx/FeedSpotlightChip";
import MakerFeedCard from "@/components/plyx/MakerFeedCard";

// Drop a maker spotlight into the swipeable feed every Nth slide.
const MAKER_INTERLEAVE_EVERY = 9;

const StandaloneExitChip = () => {
  const [open, setOpen] = useState(false);
  // Only show the "Preview · Exit" chip in the preview environment (e.g.
  // preview.emergentagent.com). On the real Plyx domain (plyx.co.uk) we hide
  // it so visitors don't see a preview tag on the live product.
  if (!isPlyxStandaloneMode() || isPlyxProductionDomain()) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="plyx-feed-exit-preview"
        title="Exit standalone Plyx preview"
        className="fixed top-3 left-3 z-40 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-semibold text-[#00E0C7] border border-[#00E0C7]/40 bg-black/60 backdrop-blur hover:bg-[#00E0C7]/15 hover:text-white transition-colors cursor-pointer"
      >
        <span>Preview</span>
        <span className="opacity-50">·</span>
        <span className="text-white/80">Exit</span>
      </button>
      <PlyxExitModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const PlyxFeed = () => {
  const [videos, setVideos] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [commentVideo, setCommentVideo] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem("plyx_feed_tab") || "foryou");
  const [makerPool, setMakerPool] = useState([]);
  const containerRef = useRef(null);
  const { user } = usePlyxAuth();

  const loadMore = useCallback(async (currentCursor, currentTab) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("tab", currentTab);
      if (currentCursor) params.set("cursor", currentCursor);
      const { data } = await plyxApi.get(`/plyx/feed/v2?${params.toString()}`);
      setVideos((prev) => (currentCursor ? [...prev, ...data.items] : data.items));
      setCursor(data.next_cursor);
    } catch (e) {
      toast.error("Could not load feed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch a small pool of featured makers once on mount. We rotate
  // through them as the user scrolls so the same maker isn't seen twice
  // in the same session. We personalise the order by detecting the
  // viewer's country (server-side geo-IP) and surfacing makers from
  // their own country first.
  useEffect(() => {
    let alive = true;
    (async () => {
      let nearCountry = "";
      try {
        const geo = await api.get("/geo/ip-detect");
        nearCountry = geo?.data?.country_name || "";
      } catch { /* geo is best-effort */ }
      const q = new URLSearchParams({ limit: "12", sort: "featured" });
      if (nearCountry) q.set("near_country", nearCountry);
      try {
        const r = await api.get(`/makers/showcases?${q.toString()}`);
        if (alive) setMakerPool(r.data?.items || []);
      } catch { /* feed still works without makers */ }
    })();
    return () => { alive = false; };
  }, []);

  // Stable interleaved slide list: every Nth slot becomes a maker card.
  // This keeps the same set of indices stable across re-renders so the
  // IntersectionObserver doesn't churn.
  const slides = useMemo(() => {
    if (videos.length === 0) return [];
    if (makerPool.length === 0) {
      return videos.map((v, i) => ({ kind: "video", key: v.video_id, idx: i, video: v }));
    }
    const out = [];
    let videoCursor = 0;
    let makerCursor = 0;
    let slideIdx = 0;
    while (videoCursor < videos.length) {
      // Inject a maker every MAKER_INTERLEAVE_EVERY slides, starting
      // around position 4 so the first taste of the feed is video.
      const isMakerSlot = slideIdx >= 4 && (slideIdx - 4) % MAKER_INTERLEAVE_EVERY === 0;
      if (isMakerSlot && makerPool.length > 0) {
        const m = makerPool[makerCursor % makerPool.length];
        out.push({ kind: "maker", key: `mkr-${m.showcase_id}-${slideIdx}`, idx: slideIdx, showcase: m });
        makerCursor += 1;
      } else {
        const v = videos[videoCursor];
        out.push({ kind: "video", key: v.video_id, idx: slideIdx, video: v });
        videoCursor += 1;
      }
      slideIdx += 1;
    }
    return out;
  }, [videos, makerPool]);

  // When tab changes — reset list & reload
  useEffect(() => {
    localStorage.setItem("plyx_feed_tab", tab);
    setVideos([]);
    setCursor(null);
    setActiveIdx(0);
    loadMore(null, tab);
  }, [tab, loadMore]);

  // Plyx creator dopamine toasts — attribution sales, new followers, and
  // view milestones. Polled on mount + every 60s while feed is open.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fmtCount = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(0)}M` :
                          n >= 1_000 ? `${(n/1_000).toFixed(0)}K` : String(n);
    const fetchNotifs = async () => {
      try {
        const { data } = await plyxApi.get("/plyx/attribution/notifications");
        if (cancelled) return;
        const events = [];
        (data.items || []).forEach((c) => {
          const sym = ({ USD: "$", GBP: "£", EUR: "€", CAD: "C$", NGN: "₦" })[c.currency] || `${c.currency} `;
          events.push({
            msg: `+${sym}${c.amount.toFixed(2)} earned · your video drove a sale 🎉`,
          });
        });
        (data.new_followers || []).forEach((f) => {
          events.push({
            msg: `@${f.handle || "someone"} just followed you 👋`,
          });
        });
        (data.milestones || []).forEach((m) => {
          const reward = Number(m.reward_amount) || 0;
          let msg;
          if (m.lifetime_first && reward > 0) {
            msg = `🏆 ${fmtCount(m.threshold)} club unlocked · £${reward.toFixed(2)} earned!`;
          } else if (reward > 0) {
            msg = `🚀 Your video just hit ${fmtCount(m.threshold)} views! · £${reward.toFixed(2)} unlocked`;
          } else {
            msg = `🚀 Your video just hit ${fmtCount(m.threshold)} views!`;
          }
          events.push({ msg });
        });
        events.forEach((e, i) => setTimeout(() => toast.success(e.msg, { duration: 6000 }), i * 1200));
      } catch { /* fire-and-forget */ }
    };
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // Detect which card is in view via IntersectionObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.7) {
            const idx = Number(entry.target.getAttribute("data-idx"));
            if (!Number.isNaN(idx)) setActiveIdx(idx);
          }
        }
        // Prefetch when nearing end
        const lastEntry = entries[entries.length - 1];
        if (lastEntry?.isIntersecting && cursor) {
          const lastIdx = Number(lastEntry.target.getAttribute("data-idx"));
          if (lastIdx >= slides.length - 3) loadMore(cursor, tab);
        }
      },
      { root: el, threshold: [0.7] }
    );
    el.querySelectorAll("[data-plyx-slide]").forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [slides.length, cursor, tab, loadMore]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black text-white flex items-center justify-center" data-testid="plyx-feed-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2"/> Loading Plyx…
      </div>
    );
  }

  if (videos.length === 0) {
    return <EmptyFeed user={user}/>;
  }

  return (
    <div className="fixed inset-0 bg-black" data-testid="plyx-feed">
      <StandaloneExitChip />
      <FeedSpotlightChip />
      <div
        ref={containerRef}
        className="h-full overflow-y-auto snap-y snap-mandatory scrollbar-none"
        style={{ scrollSnapType: "y mandatory" }}
      >
        {slides.map((s) => (
          <div
            key={s.key}
            data-plyx-slide
            data-idx={s.idx}
            className="h-full w-full snap-start snap-always"
          >
            {s.kind === "maker" ? (
              <MakerFeedCard showcase={s.showcase} />
            ) : (
              <VideoCard
                video={s.video}
                isActive={s.idx === activeIdx}
                onCommentClick={(vv) => setCommentVideo(vv)}
              />
            )}
          </div>
        ))}
        {cursor && (
          <div className="h-full w-full flex items-center justify-center text-white/60 snap-start" data-testid="plyx-feed-loading-more">
            <Loader2 className="w-5 h-5 animate-spin"/>
          </div>
        )}
      </div>

      {/* Floating top-right CTAs */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
        <Link to="/plyx" data-testid="plyx-feed-logo" className="inline-flex items-center gap-1.5">
          <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
            <defs>
              <linearGradient id="plyxFeedGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#FF2D7A"/>
                <stop offset="0.5" stopColor="#FFB42D"/>
                <stop offset="1" stopColor="#00E0C7"/>
              </linearGradient>
            </defs>
            <polygon points="14,10 14,54 52,32" fill="url(#plyxFeedGrad)"/>
          </svg>
          <span className="font-display text-lg text-white">Plyx</span>
        </Link>
        {/* Feed tabs */}
        <div className="hidden sm:flex items-center gap-1 ml-2" data-testid="plyx-feed-tabs">
          {[
            { k: "foryou", label: "For You" },
            { k: "following", label: "Following" },
            { k: "new", label: "New" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`plyx-tab-${t.k}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                tab === t.k ? "bg-white text-[#0A0A0A]" : "text-white/60 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="absolute top-4 right-16 z-10 flex items-center gap-2">
        {user ? (
          <>
            <Link
              to="/plyx/wallet"
              data-testid="plyx-nav-coins"
              title={`${(user.coin_balance ?? 0).toLocaleString()} coins`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-[#FFB42D] to-[#FF2D7A] text-[#0A0A0A] hover:opacity-90"
            >
              <Coins className="w-3 h-3" />
              {(user.coin_balance ?? 0).toLocaleString()}
            </Link>
            <Link
              to="/plyx/upload"
              data-testid="plyx-feed-upload-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-[#0A0A0A] text-xs"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              <Plus className="w-3.5 h-3.5"/> Upload
            </Link>
          </>
        ) : (
          <Link
            to="/plyx/register"
            data-testid="plyx-feed-signup-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-[#0A0A0A] text-xs"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            <Sparkles className="w-3.5 h-3.5"/> Join Plyx
          </Link>
        )}
      </div>

      {/* About link, bottom-left tiny */}
      <Link to="/plyx/about" className="absolute bottom-3 left-3 z-10 text-white/30 hover:text-white/80 text-xs flex items-center gap-1" data-testid="plyx-feed-about-link">
        <Info className="w-3 h-3"/> About Plyx
      </Link>
      <Link to="/plyx/leaderboard" className="absolute bottom-3 right-3 z-10 text-white/30 hover:text-[#FFB42D] text-xs flex items-center gap-1" data-testid="plyx-feed-leaderboard-link">
        <Trophy className="w-3 h-3"/> Leaderboard
      </Link>

      {/* Comment drawer */}
      {commentVideo && (
        <CommentDrawer
          video={commentVideo}
          onClose={() => setCommentVideo(null)}
          onCommentPosted={(videoId) => {
            setVideos((vs) => vs.map((v) => v.video_id === videoId ? { ...v, comment_count: (v.comment_count || 0) + 1 } : v));
          }}
        />
      )}
    </div>
  );
};

const EmptyFeed = ({ user }) => (
  <div className="fixed inset-0 bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="plyx-feed-empty">
    <StandaloneExitChip />
    <div className="max-w-md text-center">
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="mx-auto mb-6">
        <defs>
          <linearGradient id="emptyGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FF2D7A"/>
            <stop offset="0.5" stopColor="#FFB42D"/>
            <stop offset="1" stopColor="#00E0C7"/>
          </linearGradient>
        </defs>
        <polygon points="14,10 14,54 52,32" fill="url(#emptyGrad)"/>
      </svg>
      <h1 className="font-display text-4xl mb-3">Plyx is just getting started</h1>
      <p className="text-white/70 mb-8">No videos yet. Be one of the first to post — moments become movements here.</p>
      {user ? (
        <Link
          to="/plyx/upload"
          data-testid="plyx-empty-upload-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[#0A0A0A]"
          style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
        >
          <Plus className="w-5 h-5"/> Upload the first video
        </Link>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/plyx/register"
            data-testid="plyx-empty-register-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-[#0A0A0A]"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            Join Plyx
          </Link>
          <Link
            to="/plyx/login"
            data-testid="plyx-empty-login-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:bg-white/10 text-sm font-semibold"
          >
            I already have a Plyx account
          </Link>
        </div>
      )}
      <Link to="/plyx/about" className="block mt-8 text-xs text-white/40 hover:text-white/80" data-testid="plyx-empty-about-link">
        Learn about Plyx →
      </Link>
    </div>
  </div>
);

const CommentDrawer = ({ video, onClose, onCommentPosted }) => {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const { user } = usePlyxAuth();

  useEffect(() => {
    setLoading(true);
    plyxApi.get(`/plyx/videos/${video.video_id}/comments`)
      .then((r) => setComments(r.data))
      .catch(() => toast.error("Could not load comments"))
      .finally(() => setLoading(false));
  }, [video.video_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || !user) return;
    setPosting(true);
    try {
      const { data } = await plyxApi.post(`/plyx/videos/${video.video_id}/comments`, { body: body.trim() });
      setComments((c) => [data, ...c]);
      setBody("");
      onCommentPosted?.(video.video_id);
    } catch {
      toast.error("Could not post comment");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-20 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="plyx-comment-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-[#1a1a1a] text-white rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-semibold">Comments · {video.comment_count + (comments.length > video.comment_count ? 0 : 0)}</h3>
          <button onClick={onClose} aria-label="Close" className="text-white/60 hover:text-white" data-testid="plyx-comment-close">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center text-white/60 py-8"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-center text-white/40 text-sm py-8">No comments yet. Be the first.</div>
          ) : comments.map((c) => (
            <div key={c.comment_id} className="flex gap-3" data-testid={`plyx-comment-${c.comment_id}`}>
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-xs font-semibold shrink-0">
                {c.author?.display_name?.slice(0, 1) || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-semibold mr-2">@{c.author?.handle || "user"}</span>
                  <span className="text-white/80">{c.body}</span>
                </div>
                <div className="text-xs text-white/40 mt-1">{c.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
            </div>
          ))}
        </div>
        {user ? (
          <form onSubmit={submit} className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              type="text"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add a comment…"
              data-testid="plyx-comment-input"
              maxLength={500}
              className="flex-1 px-3 py-2 rounded-full bg-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
            <button
              type="submit"
              disabled={posting || !body.trim()}
              data-testid="plyx-comment-submit"
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#0A0A0A] disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              {posting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            </button>
          </form>
        ) : (
          <div className="p-4 border-t border-white/10 text-center text-sm text-white/60">
            <Link to="/plyx/login" className="underline hover:text-white">Log in</Link> to comment.
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxFeed;
